/**
 * Minimum Loading Duration Hook
 * Prevents loading state flicker by ensuring minimum display time
 *
 * Problem: Fast network requests can cause jarring flicker when loading states
 * appear and disappear too quickly (e.g., 50ms skeleton flash)
 *
 * Solution: Ensures loading indicators show for at least 300ms before resolving,
 * providing smooth visual transitions that users can mentally register
 */

import { useEffect, useRef, useState } from 'react';

/** Minimum time (ms) loading state should be visible to prevent flicker */
const MINIMUM_LOADING_DURATION = 300;

interface UseMinimumLoadingDurationOptions {
  /**
   * Custom minimum duration in milliseconds
   * @default 300
   */
  minimumDuration?: number;
}

/**
 * Hook that extends a loading state to last at least a minimum duration
 *
 * @param isLoading - The actual loading state from data fetching
 * @param options - Configuration options
 * @returns Extended loading state that respects minimum duration
 *
 * @example
 * ```tsx
 * const { data, isLoading: rawIsLoading } = useFetch('/api/data');
 * const isLoading = useMinimumLoadingDuration(rawIsLoading);
 * // isLoading will be true for at least 300ms even if fetch completes in 50ms
 * ```
 */
export function useMinimumLoadingDuration(
  isLoading: boolean,
  options: UseMinimumLoadingDurationOptions = {}
): boolean {
  const { minimumDuration = MINIMUM_LOADING_DURATION } = options;

  // Track extended loading state
  const [extendedLoading, setExtendedLoading] = useState(isLoading);

  // Track when loading started
  const loadingStartTime = useRef<number | null>(null);

  // Track timeout for cleanup
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Loading started
    if (isLoading && !loadingStartTime.current) {
      loadingStartTime.current = Date.now();
      setExtendedLoading(true);
    }

    // Loading finished
    if (!isLoading && loadingStartTime.current !== null) {
      const elapsed = Date.now() - loadingStartTime.current;
      const remaining = minimumDuration - elapsed;

      if (remaining > 0) {
        // Extend loading state to meet minimum duration
        timeoutRef.current = setTimeout(() => {
          setExtendedLoading(false);
          loadingStartTime.current = null;
        }, remaining);
      } else {
        // Minimum duration already met, can safely hide loading
        setExtendedLoading(false);
        loadingStartTime.current = null;
      }
    }

    // Cleanup timeout on unmount or when loading restarts
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isLoading, minimumDuration]);

  return extendedLoading;
}

/**
 * Type export for external use
 */
export type { UseMinimumLoadingDurationOptions };
